import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Popover, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { VoiceDto } from '@shared/services/apiService/apiService';
import {
  resolveVoiceProviderDisplayName,
  voiceMatchesProviderQuery,
} from '@shared/utils/voiceDisplay';
import { SELECT_VOICES_LIST_MAX_HEIGHT, useStyles } from './styles';

type SelectVoicesInputProps = {
  voices: VoiceDto[] | undefined;
  selectedIds: string[];
  onChange: (nextIds: string[]) => void;
  disabled?: boolean;
  portalDropdown?: boolean;
};

const MIN_LIST_HEIGHT = 120;

export const SelectVoicesInput: React.FC<SelectVoicesInputProps> = ({
  voices,
  selectedIds,
  onChange,
  disabled,
  portalDropdown = false,
}) => {
  const theme = useTheme();
  const { classes } = useStyles();
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const controlRef = useRef<HTMLDivElement | null>(null);
  const dropdownPortalRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const ignoreOutsideCloseRef = useRef(false);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [listMaxHeight, setListMaxHeight] = useState(SELECT_VOICES_LIST_MAX_HEIGHT);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const voiceById = useMemo(() => {
    const map = new Map<string, VoiceDto>();
    (voices ?? []).forEach((voice) => map.set(voice.id, voice));
    return map;
  }, [voices]);

  const selectedVoices = useMemo(
    () =>
      selectedIds
        .map((id) => voiceById.get(id))
        .filter((voice): voice is VoiceDto => voice !== undefined),
    [selectedIds, voiceById],
  );

  const filteredVoices = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return voices ?? [];
    return (voices ?? []).filter((voice) => {
      const name = voice.name.toLowerCase();
      return name.includes(q) || voiceMatchesProviderQuery(voice, q);
    });
  }, [voices, query]);

  const placeholder = t('superAdminMainPage.companies.selectVoicesInput.placeholder');
  const showInputPlaceholder = selectedVoices.length === 0 && !query;

  const closeDropdown = () => {
    setOpen(false);
    setQuery('');
    setActiveIndex(0);
  };

  const openDropdown = () => {
    if (disabled) return;
    setOpen(true);
  };

  const updateListMaxHeight = useCallback(() => {
    if (!controlRef.current) return;
    const rect = controlRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - 16;
    setListMaxHeight(Math.max(MIN_LIST_HEIGHT, Math.min(SELECT_VOICES_LIST_MAX_HEIGHT, spaceBelow)));
  }, []);

  useEffect(() => {
    if (!open) return;
    updateListMaxHeight();
    window.addEventListener('resize', updateListMaxHeight);
    window.addEventListener('scroll', updateListMaxHeight, true);
    return () => {
      window.removeEventListener('resize', updateListMaxHeight);
      window.removeEventListener('scroll', updateListMaxHeight, true);
    };
  }, [open, updateListMaxHeight]);

  useEffect(() => {
    if (filteredVoices.length === 0) {
      setActiveIndex(-1);
      return;
    }
    if (activeIndex >= filteredVoices.length || activeIndex < 0) {
      setActiveIndex(0);
    }
  }, [filteredVoices, activeIndex]);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (ignoreOutsideCloseRef.current) return;
      const target = event.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (dropdownPortalRef.current?.contains(target)) return;
      closeDropdown();
    };
    const listenerId = window.setTimeout(() => {
      document.addEventListener('mousedown', handlePointerDown);
    }, 0);
    return () => {
      window.clearTimeout(listenerId);
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [open]);

  const toggleVoice = (voiceId: string) => {
    if (selectedSet.has(voiceId)) {
      onChange(selectedIds.filter((id) => id !== voiceId));
      return;
    }
    onChange([...selectedIds, voiceId]);
    setQuery('');
    inputRef.current?.focus();
  };

  const removeVoice = (event: React.MouseEvent, voiceId: string) => {
    event.stopPropagation();
    onChange(selectedIds.filter((id) => id !== voiceId));
    inputRef.current?.focus();
  };

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeDropdown();
      inputRef.current?.blur();
      return;
    }
    if (event.key === 'Backspace' && !query && selectedIds.length > 0) {
      onChange(selectedIds.slice(0, -1));
      return;
    }
    if (!filteredVoices.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) setOpen(true);
      setActiveIndex((prev) => (prev + 1) % filteredVoices.length);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) setOpen(true);
      setActiveIndex((prev) => (prev - 1 + filteredVoices.length) % filteredVoices.length);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      if (activeIndex >= 0) toggleVoice(filteredVoices[activeIndex].id);
    }
  };

  const handleControlMouseDown = (event: React.MouseEvent) => {
    if (disabled) return;
    if ((event.target as HTMLElement).closest('button')) return;
    event.preventDefault();
    ignoreOutsideCloseRef.current = true;
    openDropdown();
    inputRef.current?.focus();
    window.requestAnimationFrame(() => {
      ignoreOutsideCloseRef.current = false;
    });
  };

  const listContent = (
    <Box
      id="select-voices-listbox"
      role="listbox"
      className={classes.list}
      aria-multiselectable
      style={{ maxHeight: listMaxHeight }}>
      {filteredVoices.length === 0 ? (
        <Box className={classes.empty}>{t('campaignPage.voicePicker.empty')}</Box>
      ) : (
        filteredVoices.map((voice, idx) => {
          const isSelected = selectedSet.has(voice.id);
          const isActive = activeIndex === idx;
          return (
            <button
              key={voice.id}
              type="button"
              role="option"
              aria-selected={isSelected}
              className={classNames(classes.row, {
                [classes.rowActive]: isActive && !isSelected,
                [classes.rowSelected]: isSelected,
              })}
              onMouseEnter={() => setActiveIndex(idx)}
              onClick={() => toggleVoice(voice.id)}>
              <span className={classes.rowTitle}>{voice.name}</span>
              <span className={classes.providerBadge}>
                {resolveVoiceProviderDisplayName(voice)}
              </span>
              {isSelected ? <CheckIcon className={classes.checkIcon} /> : null}
            </button>
          );
        })
      )}
    </Box>
  );

  return (
    <Box ref={rootRef} className={classNames(classes.root, { [classes.rootOpen]: open && !portalDropdown })}>
      <Box
        ref={controlRef}
        className={classNames(classes.control, {
          [classes.controlFocused]: open,
          [classes.controlDisabled]: disabled,
        })}
        onMouseDown={handleControlMouseDown}>
        {selectedVoices.length > 0 ? (
          <Box className={classes.chipsScroll}>
            {selectedVoices.map((voice) => (
              <span key={voice.id} className={classes.chip}>
                <span className={classes.chipLabel}>{voice.name}</span>
                <button
                  type="button"
                  className={classes.chipRemove}
                  disabled={disabled}
                  aria-label={t('superAdminMainPage.companies.selectVoicesInput.removeVoice', {
                    name: voice.name,
                  })}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={(event) => removeVoice(event, voice.id)}>
                  <CloseIcon sx={{ fontSize: 14 }} />
                </button>
              </span>
            ))}
          </Box>
        ) : null}
        <Box className={classes.inputRow}>
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-controls="select-voices-listbox"
            aria-autocomplete="list"
            disabled={disabled}
            className={classes.inlineInput}
            placeholder={showInputPlaceholder ? placeholder : undefined}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              openDropdown();
            }}
            onFocus={openDropdown}
            onKeyDown={onInputKeyDown}
          />
        </Box>
        <span className={classes.chevron}>
          <KeyboardArrowDownIcon fontSize="small" />
        </span>
      </Box>

      {open && !disabled && portalDropdown ? (
        <Popover
          open
          anchorEl={controlRef.current}
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          marginThreshold={0}
          slotProps={{
            root: {
              sx: { zIndex: theme.zIndex.modal + 1 },
            },
            paper: {
              className: classes.dropdownPortalPaper,
              style: { width: controlRef.current?.offsetWidth },
            },
          }}>
          <Box
            ref={dropdownPortalRef}
            onMouseDown={(event) => event.preventDefault()}
            sx={{ width: '100%' }}>
            {listContent}
          </Box>
        </Popover>
      ) : null}

      {open && !disabled && !portalDropdown ? (
        <Box className={classes.dropdown} onMouseDown={(event) => event.preventDefault()}>
          {listContent}
        </Box>
      ) : null}
    </Box>
  );
};
